import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaArrowLeft } from 'react-icons/fa';

import Loading from '../../../../components/Loading';
import Modal from '../../../../components/Modal/Modal';
import usePermissions from '../../../../components/Menu/MenuItems/hooks/usePermissions';
import { Container } from '../../../../styles/layout/Container';
import { EmptyState } from '../../../../styles/feedback/EmptyState';
import {
   EntityActionBar,
   ActionBarSpacer,
} from '../../../../styles/ui/ActionBar';
import {
   PrimaryButton,
   CtaButton,
   DangerButton,
   GhostButton,
} from '../../../../styles/ui/Button';
import {
   getCandidateOnboarding,
   updateOnboardingStatus,
} from '../../../../services/hr/recruitment/onboardingApi';
import { listDependents } from '../../../../services/hr/recruitment/dependentsApi';
import { listDocuments } from '../../../../services/hr/recruitment/admissionDocumentsApi';
import { getCandidate } from '../../../../services/hr/recruitment/candidatesApi';
import { listUserDirectory } from '../../../../services/users/usersApi';
import { formatUserLabel } from '../../../../utils/format';

import OnboardingStatusBadge from '../components/OnboardingStatusBadge';
import {
   ONBOARDING_STATUS,
   ONBOARDING_STATUS_LABEL,
   isOnboardingTerminal,
} from '../constants';
import { formatCpf, formatDateBr, initialsOf } from '../utils/format';
import { recruitmentErrorMessage } from '../utils/apiError';
import { advanceGateFor } from './advanceGates';

import PipelineStepper from './components/PipelineStepper';
import StepWorkPanel from './components/StepWorkPanel';
import ManualStepModal from './components/ManualStepModal';
import AdmissionRail from './components/AdmissionRail';
import PreCadastroCard from './components/PreCadastroCard';
import DependentsCard from './components/DependentsCard';
import DocumentsChecklistCard from './components/DocumentsChecklistCard';
import DisqualifyModal from './components/DisqualifyModal';
import AdmitModal from './components/AdmitModal';
import {
   HeaderBar,
   HeaderMain,
   Avatar,
   TitleRow,
   MetaLine,
   BackRow,
   WorkGrid,
} from './styles';

// ---------------------------------------------------------------------------
// RH · Recrutamento — Onboarding de admissão (handoff 0065, Fase 1). Página
// dedicada, acessada pelo card da ficha do candidato (`:id` = candidateId).
// Orquestra: pipeline (avanço um-passo com confirmação) + passos manuais +
// pré-cadastro + dependentes + checklist + admitir/desqualificar.
//
// `fetchAll` recarrega onboarding + dependentes + documentos JUNTOS: mutações
// de pré-cadastro/dependentes regeneram o checklist no backend — recarregar
// parcial deixaria a tela divergente.
// ---------------------------------------------------------------------------

export default function OnboardingShow() {
   const { id: candidateId } = useParams();
   const navigate = useNavigate();
   const location = useLocation();
   const { hasPermission } = usePermissions();

   const canUpdate = hasPermission('hr:candidate_onboarding:update');
   const canAdmit = hasPermission('hr:candidate_onboarding:admit');
   const canManageDependents = hasPermission('hr:candidate_dependent:manage');
   const canManageDocs = hasPermission('hr:candidate_document:manage');

   const [onboarding, setOnboarding] = useState(null);
   const [candidate, setCandidate] = useState(null);
   const [dependents, setDependents] = useState([]);
   const [documents, setDocuments] = useState([]);
   const [isLoading, setIsLoading] = useState(true);
   const [notFound, setNotFound] = useState(false);

   // Modais / ações.
   const [advanceTarget, setAdvanceTarget] = useState(null);
   const [isAdvancing, setIsAdvancing] = useState(false);
   const [disqualifyOpen, setDisqualifyOpen] = useState(false);
   const [admitOpen, setAdmitOpen] = useState(false);
   // Edição retroativa de passo manual (aberta pelo lápis do rail).
   const [manualStepTarget, setManualStepTarget] = useState(null);

   // Usuários (nome do responsável do RH) — 1 fetch, compartilhado entre o
   // rail e o select do pré-cadastro.
   const [users, setUsers] = useState([]);
   useEffect(() => {
      let ignore = false;
      // `GET /users/directory?status=all` (handoff 0110 §3.9). `all` porque
      // esta mesma lista resolve o NOME do responsável já registrado, e passo
      // de onboarding não perde o autor porque a conta dele foi excluída
      // depois. No select do pré-cadastro, ex-usuários aparecem marcados
      // "(inativo)" — quem escolhe vê o que está escolhendo.
      listUserDirectory({ status: 'all' })
         .then((list) => {
            if (!ignore) setUsers(list);
         })
         .catch(() => {
            /* enriquecimento opcional: sem nomes, rail mostra "—" */
         });
      return () => {
         ignore = true;
      };
   }, []);

   const fetchAll = useCallback(async () => {
      setIsLoading(true);
      try {
         // Listas falham independentes (não derrubam a página inteira).
         const [onboardingResult, dependentList, documentList] =
            await Promise.all([
               getCandidateOnboarding(candidateId),
               listDependents(candidateId).catch(() => []),
               listDocuments(candidateId).catch(() => []),
            ]);

         if (!onboardingResult) {
            setNotFound(true);
            return;
         }

         setOnboarding(onboardingResult);
         setDependents(dependentList);
         setDocuments(documentList);

         // O backend costuma embutir `candidate` no onboarding; fallback pro
         // GET do candidato quando não vier populado.
         if (onboardingResult.candidate) {
            setCandidate(onboardingResult.candidate);
         } else {
            const candidateResult = await getCandidate(candidateId).catch(
               () => null,
            );
            setCandidate(candidateResult);
         }
      } catch (err) {
         toast.error(
            recruitmentErrorMessage(err, 'Erro ao carregar o onboarding.'),
         );
         setNotFound(true);
      } finally {
         setIsLoading(false);
      }
   }, [candidateId]);

   useEffect(() => {
      fetchAll();
   }, [fetchAll]);

   const handleBack = () => {
      if (location.key !== 'default') navigate(-1);
      else navigate(`/hr/recruitment/candidates/${candidateId}`);
   };

   const handleConfirmAdvance = async () => {
      if (!advanceTarget || isAdvancing) return;
      setIsAdvancing(true);
      try {
         const { message } = await updateOnboardingStatus(
            onboarding.id,
            advanceTarget,
         );
         toast.success(message);
      } catch (err) {
         toast.error(
            recruitmentErrorMessage(err, 'Erro ao avançar a etapa.'),
         );
      } finally {
         setIsAdvancing(false);
         setAdvanceTarget(null);
         // Recarrega SEMPRE — no erro de transição o estado local estava velho.
         fetchAll();
      }
   };

   if (isLoading && !onboarding) {
      return (
         <Container>
            <Loading isLoading />
         </Container>
      );
   }

   if (notFound || !onboarding) {
      return (
         <Container>
            <BackRow>
               <GhostButton type="button" onClick={handleBack}>
                  <FaArrowLeft /> Voltar
               </GhostButton>
            </BackRow>
            <EmptyState
               title="Onboarding não encontrado"
               description="Este candidato ainda não tem onboarding — ele nasce quando a Entrevista 2 é aprovada."
            />
         </Container>
      );
   }

   const status = onboarding.status;
   const isTerminal = isOnboardingTerminal(status);
   const isAdmitted = status === ONBOARDING_STATUS.ADMITIDO;
   const collectedFileCount = documents.filter((doc) => doc.has_file).length;
   const employeeId = candidate?.converted_employee_id;

   const responsibleUser = onboarding.responsible_hr_user_id
      ? users.find(
           (user) =>
              String(user.id) === String(onboarding.responsible_hr_user_id),
        )
      : null;
   const responsibleName = responsibleUser
      ? formatUserLabel(responsibleUser)
      : null;

   // Trava de completude da etapa atual (campos obrigatórios = gate).
   const advanceGate = advanceGateFor(status, { onboarding, documents });

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <BackRow>
            <GhostButton type="button" onClick={handleBack}>
               <FaArrowLeft /> Voltar
            </GhostButton>
         </BackRow>

         <HeaderBar>
            <HeaderMain>
               <Avatar>{initialsOf(candidate?.full_name)}</Avatar>
               <div>
                  <TitleRow>
                     <h1>{candidate?.full_name ?? 'Onboarding'}</h1>
                     <OnboardingStatusBadge value={status} />
                  </TitleRow>
                  <MetaLine>
                     <span>
                        {onboarding.job_position?.position_name ??
                           'Cargo a definir no pré-cadastro'}
                     </span>
                     <span className="dot">·</span>
                     <span>CPF: {formatCpf(candidate?.cpf)}</span>
                     {onboarding.start_date && (
                        <>
                           <span className="dot">·</span>
                           <span>
                              Início: {formatDateBr(onboarding.start_date)}
                           </span>
                        </>
                     )}
                  </MetaLine>
               </div>
            </HeaderMain>

            <EntityActionBar>
               {canUpdate && !isTerminal && (
                  <>
                     <DangerButton
                        type="button"
                        onClick={() => setDisqualifyOpen(true)}
                     >
                        Desqualificar
                     </DangerButton>
                     <ActionBarSpacer />
                  </>
               )}
               <CtaButton
                  type="button"
                  onClick={() =>
                     navigate(`/hr/recruitment/candidates/${candidateId}`)
                  }
               >
                  Ver ficha do candidato
               </CtaButton>
               {isAdmitted && employeeId && (
                  <CtaButton
                     type="button"
                     onClick={() => navigate(`/hr/employees/${employeeId}`)}
                  >
                     Ver funcionário
                  </CtaButton>
               )}
            </EntityActionBar>
         </HeaderBar>

         <PipelineStepper
            onboarding={onboarding}
            canUpdate={canUpdate}
            canAdmit={canAdmit}
            isBusy={isAdvancing || isLoading}
            advanceGate={advanceGate}
            onRequestAdvance={(next) => setAdvanceTarget(next)}
            onRequestAdmit={() => setAdmitOpen(true)}
            stepPanel={
               <StepWorkPanel
                  onboarding={onboarding}
                  canUpdate={canUpdate && !isTerminal}
                  onSaved={fetchAll}
               />
            }
         />

         <WorkGrid>
            <div>
               <DocumentsChecklistCard
                  candidateId={candidateId}
                  documents={documents}
                  dependents={dependents}
                  canManage={canManageDocs}
                  isReadOnly={isTerminal}
                  onChanged={fetchAll}
               />

               <PreCadastroCard
                  onboarding={onboarding}
                  canUpdate={canUpdate && !isTerminal}
                  users={users}
                  onSaved={fetchAll}
               />

               <DependentsCard
                  candidateId={candidateId}
                  dependents={dependents}
                  canManage={canManageDependents}
                  isReadOnly={isTerminal}
                  onChanged={fetchAll}
               />
            </div>

            <AdmissionRail
               onboarding={onboarding}
               candidate={candidate}
               documents={documents}
               responsibleName={responsibleName}
               canEditPastSteps={canUpdate && !isTerminal}
               onEditStep={(stepKey) => setManualStepTarget(stepKey)}
            />
         </WorkGrid>

         {/* ─── Modais ──────────────────────────────────────────────────── */}
         <Modal
            isOpen={Boolean(advanceTarget)}
            onClose={() => !isAdvancing && setAdvanceTarget(null)}
            title="Avançar etapa"
            maxWidth="440px"
            footer={
               <>
                  <GhostButton
                     type="button"
                     onClick={() => setAdvanceTarget(null)}
                     disabled={isAdvancing}
                  >
                     Cancelar
                  </GhostButton>
                  <PrimaryButton
                     type="button"
                     onClick={handleConfirmAdvance}
                     disabled={isAdvancing}
                  >
                     {isAdvancing ? 'Avançando…' : 'Avançar'}
                  </PrimaryButton>
               </>
            }
         >
            <p style={{ margin: 0, fontSize: '0.9rem' }}>
               Avançar o onboarding para{' '}
               <strong>
                  {ONBOARDING_STATUS_LABEL[advanceTarget] ?? advanceTarget}
               </strong>
               ? Não é possível voltar um passo depois.
            </p>
         </Modal>

         <DisqualifyModal
            isOpen={disqualifyOpen}
            onClose={() => setDisqualifyOpen(false)}
            onboarding={onboarding}
            onSaved={fetchAll}
         />

         <ManualStepModal
            stepKey={manualStepTarget}
            onClose={() => setManualStepTarget(null)}
            onboarding={onboarding}
            onSaved={fetchAll}
         />

         <AdmitModal
            isOpen={admitOpen}
            onClose={() => setAdmitOpen(false)}
            onboarding={onboarding}
            candidate={candidate}
            collectedFileCount={collectedFileCount}
            onAdmitted={(newEmployeeId) => {
               if (newEmployeeId) navigate(`/hr/employees/${newEmployeeId}`);
               else fetchAll();
            }}
            onRefresh={fetchAll}
         />
      </Container>
   );
}
